import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Loader } from '@mantine/core';
import Navigation from '../Home/Navigation';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import styles from './CreateContent.module.css';
import { useNotification } from '../../context/NotificationContext';
import { useVideoUpload } from '../../hooks/useVideoUpload';
import { getApiUrl } from '../../config/api';
import { useMobileWarning } from '../../hooks/useMobileWarning';
import MobileWarningModal from '../EnhancedHome/MobileWarningModal';
import { 
  fetchProjects, 
  Project, 
  deleteProject, 
  duplicateProject, 
  updateProjectStage,
  ProjectStage
} from '../../services/projectService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faUpload, 
  faMicrophone, 
  faVideo, 
  faCalendarAlt, 
  faClock,
  faEllipsisVertical,
  faTrash,
  faDownload,
  faLink,
  faMagicWandSparkles
} from '@fortawesome/free-solid-svg-icons';
import Dashboard from '../Dashboard/Dashboard';
import { addAspectRatioConversionParams } from '../../utils/navigationHelpers';
import { useTranslation } from 'react-i18next';

const AIVideoEditor: React.FC = () => {
  const { t } = useTranslation('contentCreation');
  const [isLoading, setIsLoading] = useState(false);
  const [jobId, setJobId] = useState("");
  const [selectedOption, setSelectedOption] = useState<'all' | 'recent' | 'audio' | 'video'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeActionDropdown, setActiveActionDropdown] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  
  const { showNotification } = useNotification();
  const { uploadVideo, isUploading } = useVideoUpload();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoInputRef = useRef<HTMLInputElement>(null);
  
  // Mobile warning hook
  const { showMobileWarning, handleMobileWarningClose, handleMobileWarningContinue } = useMobileWarning();

  // Fetch projects when component mounts
  useEffect(() => {
    let isMounted = true;
    
    const loadProjects = async () => {
      try {
        setIsProjectsLoading(true);
        setError(null);
        
        if (!isMounted) return;
        
        const response = await fetchProjects(1, 1000);
        
        if (!isMounted) return;
        
        // If we get back response with projects property, use it even if empty
        if (response && 'projects' in response) {
          console.log('All projects from API:', response.projects.map(p => ({
            id: p.id,
            title: p.title,
            stage: p.stage
          })));
          
          // Filter for projects WITHOUT audio (video-only projects)
          const videoProjects = response.projects.filter(p => {
            const hasVideo = p.originalVideoUrl && p.originalVideoUrl.trim() !== '';
            const hasNoAudio = !p.originalAudioUrl && !p.finalAudio;
            
            console.log(`Project ${p.id} (${p.title}) - Has Video: ${hasVideo}, Has No Audio: ${hasNoAudio}`);
            return hasVideo && hasNoAudio;
          });
          
          setProjects(videoProjects);
          console.log('Filtered AI Video Editor projects (videos without audio):', 
            videoProjects.map(p => ({
              id: p.id,
              title: p.title,
              type: p.type,
              stage: p.stage
            }))
          );
        } else {
          // If API returns unexpected format, set empty array and show a message
          setProjects([]);
          console.warn('API returned unexpected data format');
        }
        
      } catch (err) {
        if (!isMounted) return;
        
        console.error('Error fetching projects:', err);
        setError('Failed to load projects. Please try again later.');
        showNotification({
          title: 'Error',
          message: 'Failed to load projects',
          type: 'error'
        });
        // Set empty projects array to prevent loading spinner from showing indefinitely
        setProjects([]);
      } finally {
        if (isMounted) {
          setIsProjectsLoading(false);
        }
      }
    };

    loadProjects();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  // Trigger hidden video file input when "Upload Video" is clicked
  const handleVideoUploadClick = () => {
    videoInputRef.current?.click();
  };

  // Handle video file selection and upload
  const handleVideoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const videoFile = event.target.files?.[0];
    if (!videoFile) return;

    try {
      setIsLoading(true);
      
      // Show notification about upload starting
      // showNotification({
      //   title: 'Upload Started',
      //   message: 'Uploading video... This may take a few minutes for large files.',
      //   type: 'info'
      // });
      
      // Upload video using the hook
      const response = await uploadVideo(videoFile);
      
      // The upload returns a jobId for async processing
      if (response.jobId) {
        setJobId(response.jobId);
        // Dashboard will show progress and call processData when complete
      } else if (response.projectId) {
        // Segmented upload completed synchronously
        if (response.isSegmented) {
          // showNotification({
          //   title: 'Upload Complete',
          //   message: `Video split into ${response.totalSegments} segments for optimal performance`,
          //   type: 'success'
          // });
        }
        // Pass empty string for mediaUrl since project already has the video
        processData('', response.projectId);
      }
      
    } catch (error) {
      console.error('Video upload failed:', error);
      // Error notification already shown by useVideoUpload hook
    } finally {
      setIsLoading(false);
    }
  };

  const processData = async (mediaUrl: string, projectId: string) => {
    setIsLoading(false);
    setUploadStatus('');
    
    console.log("Media URL---> ", mediaUrl);
    console.log("projectId---> ", projectId);
    
    try {
      // Fetch the project to determine if it's video
      const token = localStorage.getItem('authToken');
      const projectResponse = await fetch(getApiUrl(`/projects/${projectId}`), {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });
      
      if (projectResponse.ok) {
        const projectData = await projectResponse.json();
        console.log('Project data:', projectData);
        
        // Video upload - backend already created initial media item
        console.log('🎥 Video project detected, navigating to editor...');
        
        // Navigate with project-id and from_app parameter (video-only mode)
        const queryParams = new URLSearchParams();
        queryParams.append('project-id', projectId);
        queryParams.append('from_app', 'ai-video-editor');
        // No audio-url = video-only mode will be detected
        navigate(`/create-content?${queryParams.toString()}`);
      }
    } catch (error) {
      console.error('Error processing upload:', error);
      // Fallback: navigate with project-id and from_app parameter
      const queryParams = new URLSearchParams();
      queryParams.append('project-id', projectId);
      queryParams.append('from_app', 'ai-video-editor');
      navigate(`/create-content?${queryParams.toString()}`);
    }
  };

  // Toggle dropdown visibility
  const toggleActionDropdown = (projectId: string) => {
    setActiveActionDropdown(activeActionDropdown === projectId ? null : projectId);
  };

  // Handle project delete
  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      setProjects(projects.filter(project => project.id !== projectId));
      // showNotification({
      //   title: 'Success',
      //   message: 'Project deleted successfully',
      //   type: 'success'
      // });
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification({
        title: 'Delete Failed',
        message: 'There was an error deleting the project',
        type: 'error'
      });
    }
  };

  // Handle project duplication
  const handleDuplicateProject = async (projectId: string) => {
    try {
      const newProject = await duplicateProject(projectId);
      setProjects([newProject, ...projects]);
      showNotification({
        title: 'Success',
        message: 'Project duplicated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error("Duplication failed:", error);
      showNotification({
        title: 'Duplication Failed',
        message: 'There was an error duplicating the project',
        type: 'error'
      });
    }
  };

  // Handle continue working on project
  const handleContinueProject = async (project: Project) => {
    console.log('Continue project:', { 
      projectId: project.id, 
      projectType: project.type, 
      metadata: project.metadata,
      originalVideoUrl: project.originalVideoUrl,
    });
    
    try {
      // Update project stage to CONTENT_APP when navigating to content editor
      await updateProjectStage(project.id, ProjectStage.CONTENT_APP);
      
      // Navigate to video editor with from_app parameter
      const queryParams = new URLSearchParams();
      queryParams.append('project-id', project.id);
      queryParams.append('from_app', 'ai-video-editor');
      
      // ✅ Add aspect ratio conversion params for cloned projects
      addAspectRatioConversionParams(queryParams, project, '[AI VIDEO EDITOR]');
      
      // For AI Video Editor workflow, use video URL only (no audio)
      // const videoUrl = project.generatedVideoUrl || project.originalVideoUrl;
      // if (videoUrl) {
      //   queryParams.append('video-url', videoUrl);
      // }
      
      console.log('AI Video Editor navigation:', `/create-content?${queryParams.toString()}`);
      navigate(`/create-content?${queryParams.toString()}`);
    } catch (error) {
      console.error("Error updating project stage:", error);
      showNotification({
        title: 'Error',
        message: 'Failed to update project stage',
        type: 'error'
      });
      
      // Still navigate even if the stage update fails
      const queryParams = new URLSearchParams();
      queryParams.append('project-id', project.id);
      queryParams.append('from_app', 'ai-video-editor');
      
      // ✅ Also add conversion params in error case
      addAspectRatioConversionParams(queryParams, project, '[AI VIDEO EDITOR]');
      
      // const videoUrl = project.generatedVideoUrl || project.originalVideoUrl;
      // if (videoUrl) {
      //   queryParams.append('video-url', videoUrl);
      // }
      
      navigate(`/create-content?${queryParams.toString()}`);
    }
  };

  // Filter projects based on selectedOption and searchTerm
  const filteredProjects = projects.filter(project => {
    if (selectedOption === 'recent') {
      // Get projects from the last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const projectDate = new Date(project.date);
      if (projectDate < sevenDaysAgo) return false;
    }
    
    // Filter by search term
    if (searchTerm && !project.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Function to get video duration from URL
  const getVideoDuration = async (videoUrl: string): Promise<string> => {
    try {
      return new Promise((resolve) => {
        const video = document.createElement('video');
        video.addEventListener('loadedmetadata', () => {
          const duration = video.duration;
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          resolve(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        });
        
        video.addEventListener('error', () => {
          resolve('0:00');
        });
        
        video.src = videoUrl;
      });
    } catch (error) {
      console.error('Error getting video duration:', error);
      return '0:00';
    }
  };

  // Function to get project duration
  const getProjectDuration = async (project: Project): Promise<string> => {
    // If project already has a valid duration (not "00:00" or empty), use it
    if (project.duration && project.duration !== '00:00' && project.duration !== '0:00') {
      return project.duration;
    }
    
    // Otherwise, calculate from video URL
    const videoUrl = project.originalVideoUrl;
    if (videoUrl) {
      return await getVideoDuration(videoUrl);
    }
    
    return '0:00';
  };

  // State to store calculated durations
  const [projectDurations, setProjectDurations] = useState<Record<string, string>>({});

  // Effect to calculate durations for projects
  useEffect(() => {
    const calculateDurations = async () => {
      const newDurations: Record<string, string> = {};
      
      for (const project of projects) {
        const duration = await getProjectDuration(project);
        newDurations[project.id] = duration;
      }
      
      setProjectDurations(newDurations);
    };

    if (projects.length > 0) {
      calculateDurations();
    }
  }, [projects]);

  // Helper function to get video URL
  const getVideoUrl = (project: Project): string | null => {
    const videoUrl = project.generatedVideoUrl || project.originalVideoUrl;
    
    if (!videoUrl) return null;
    
    return videoUrl;
  };
  const getDownloadVideoUrl = (project: Project): string | null => {
    const videoUrl = project.captionedVideoUrl ||  project.generatedVideoUrl;
    
    if (!videoUrl) return null;
    
    return videoUrl;
  };
  

  // Function to format the date in a more readable way
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className={styles.outerContainer}>
      <SEO
        title="AI Video Editor - Kingdom Studio"
        description="Edit your videos without audio using our AI-powered video editor. Professional video editing made simple."
        keywords="ai video editor, video editing, no audio video, video processor"
        url="https://kingdomstudio.pro/ai-video-editor"
      />
      <Navigation />
      
      {/* Mobile Warning Modal */}
      <MobileWarningModal 
        show={showMobileWarning}
        onClose={handleMobileWarningClose}
        onContinue={handleMobileWarningContinue}
      />
      
      <main className={styles.container}>
        <div className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.header}>
              <h1 className={styles.title}>
                <FontAwesomeIcon icon={faVideo} className={styles.titleIcon} />
                {t('contentCreation.videoEditor.title')}
              </h1>
              <p className={styles.subtitle}>
                {t('contentCreation.videoEditor.subtitle')}
              </p>
              
              {/* Action Button */}
              <div className={styles.headerActions}>
                <Button
                  className={styles.createButton}
                  onClick={handleVideoUploadClick}
                  leftSection={<FontAwesomeIcon icon={faVideo} />}
                  size="lg"
                  loading={isUploading}
                  disabled={isLoading || isUploading}
                >
                  {isUploading ? t('contentCreation.videoEditor.uploading') : t('contentCreation.videoEditor.uploadButton')}
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Hidden video file input */}
        <input
          type="file"
          accept="video/*"
          ref={videoInputRef}
          style={{ display: 'none' }}
          onChange={handleVideoFileChange}
        />
        
        {/* Dashboard component for showing upload progress */}
        {jobId && (
          <div className={styles.dashboardContainer}>
            <Dashboard jobId={jobId} callBackFunc={processData} displayOption="fullview" />
          </div>
        )}
        
        {/* Projects Section */}
        <div className={styles.projectsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('contentCreation.videoEditor.projectsTitle')}</h2>
            <div className={styles.searchAndFilter}>
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder={t('contentCreation.projectList.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              
              <div className={styles.filterOptions}>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'all' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('all')}
                >
                  {t('contentCreation.projectList.filters.all')} ({projects.length})
                </button>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'recent' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('recent')}
                >
                  {t('contentCreation.projectList.filters.recent')}
                </button>
              </div>
            </div>
          </div>
          
          {isProjectsLoading ? (
            <div className={styles.loadingContainer}>
              <Loader size="xl" />
              <p>{t('contentCreation.projectList.loading')}</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <p className={styles.errorMessage}>{error}</p>
              <Button onClick={() => window.location.reload()} className={styles.retryButton}>
                Retry
              </Button>
            </div>
          ) : projects.length > 0 ? (
            <div className={styles.projectGrid}>
              {filteredProjects.map(project => (
                <div 
                  key={project.id} 
                  className={`${styles.projectCard} 
                    ${activeActionDropdown === project.id ? styles.cardWithActiveDropdown : ''}
                    ${getVideoUrl(project) ? styles.hasGeneratedVideo : ''}
                  `}
                >
                  {getDownloadVideoUrl(project) && (
                    <a 
                      href={getDownloadVideoUrl(project)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className={styles.videoAvailableBadge}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </a>
                  )}
                  <div className={styles.projectThumbnail}>
                    {getVideoUrl(project) ? (
                      <video 
                        src={getVideoUrl(project)!}
                        className={styles.projectVideo}
                        muted
                        preload="metadata"
                        poster=""
                        onLoadedData={(e) => {
                          const video = e.target as HTMLVideoElement;
                          video.currentTime = 0.1;
                        }}
                        onLoadedMetadata={(e) => {
                          const video = e.target as HTMLVideoElement;
                          video.currentTime = Math.min(1, video.duration * 0.1);
                        }}
                        onError={(e) => {
                          console.error('❌ Video failed to load for project:', project.title);
                          const videoElement = e.target as HTMLVideoElement;
                          const img = document.createElement('img');
                          img.src = project.thumbnail || 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg';
                          img.alt = project.title;
                          img.className = styles.projectThumbnail;
                          img.onerror = () => {
                            img.src = 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg';
                          };
                          videoElement.parentNode?.replaceChild(img, videoElement);
                        }}
                      />
                    ) : (
                      <img 
                        src={project.thumbnail || 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg'}
                        alt={project.title}
                        className={styles.projectthumbnail}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg';
                        }} 
                      />
                    )}
                    <div className={styles.projectType}>
                      <FontAwesomeIcon icon={faVideo} />
                    </div>
                    <div className={styles.projectDuration}>
                      <FontAwesomeIcon icon={faClock} />
                      <span>{projectDurations[project.id] || project.duration || '0:00'}</span>
                    </div>
                  </div>
                  
                  <div className={styles.projectInfo}>
                    <h3 className={styles.projectTitle}>{project.title}</h3>
                    <div className={styles.projectMeta}>
                      <span className={styles.projectDate}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {formatDate(project.date)}
                      </span>
                    </div>
                  </div>
                  
                  <div className={styles.projectActions}>
                    <Button 
                      className={styles.continueButton}
                      onClick={() => handleContinueProject(project)}
                    >
                      Continue
                    </Button>
                    <div className={styles.moreActions}>
                      <button 
                        className={styles.moreButton}
                        onClick={() => toggleActionDropdown(project.id)}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </button>
                      {activeActionDropdown === project.id && (
                        <div className={`${styles.actionDropdown} ${activeActionDropdown === project.id ? styles.active : ''}`}
                             onClick={(e) => e.stopPropagation()}
                        >
                          {getDownloadVideoUrl(project) && (
                            <a 
                              className={styles.actionButton}
                              href={getDownloadVideoUrl(project)!}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                            >
                              <FontAwesomeIcon icon={faDownload} className="fa-download" /> Download Video
                            </a>
                          )}
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDuplicateProject(project.id)}
                          >
                            Duplicate
                          </button>
                          <button className={styles.actionButton}>Share</button>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} className="fa-trash" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noProjects}>
              <FontAwesomeIcon icon={faPlus} size="2x" className={styles.noProjectsIcon} />
              <h3>{t('contentCreation.videoEditor.noProjects')}</h3>
              <p>{t('contentCreation.videoEditor.noProjectsMessage')}</p>
              <Button 
                className={styles.createNewButton}
                onClick={handleVideoUploadClick}
                disabled={isLoading || isUploading}
                leftSection={<FontAwesomeIcon icon={faVideo} />}
              >
                {t('contentCreation.videoEditor.uploadButton')}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AIVideoEditor;
